@file:OptIn(ExperimentalMaterial3Api::class)

package com.wintermar.inspection.feature.presentation.template.landing

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.SheetState
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.modal.util.ModalAction
import com.wintermar.inspection.core.ui.component.modal.util.ModalActionModel
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.core.ui.widget.EmptyContent
import com.wintermar.inspection.core.ui.widget.LoadingDialog
import com.wintermar.inspection.core.ui.widget.Pagination
import com.wintermar.inspection.core.ui.widget.PullToRefreshContainer
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.presentation.template.widget.preview.TemplatePreview
import com.wintermar.inspection.feature.util.expect_init.LandingHeader
import com.wintermar.inspection.feature.util.expect_init.setupLandingContent
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.auditor
import inspection.composeapp.generated.resources.create_inspection
import inspection.composeapp.generated.resources.no_templates_found
import inspection.composeapp.generated.resources.start_inspection
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun TemplateLandingScreen(
    paddingValues: PaddingValues,
    viewModel: TemplateLandingViewModel = koinViewModel(),
    color: ThemeColor = LocalAppTheme.current.color,
    dimension: AppDimension = LocalAppTheme.current.dimension,
    onStartInspection: (inspectionId: String) -> Unit
) {
    val state = viewModel.state
    val sheetState = rememberModalBottomSheetState(
        skipPartiallyExpanded = true
    )
    var topPadding by remember { mutableStateOf(dimension.spacing.space0) }

    LaunchedEffect(Unit) {
        setupLandingContent(dimension) { topPaddingContent, _ ->
            topPadding = topPaddingContent
        }

        viewModel.navigationEvent.collect { event ->
            when (event) {
                is TemplateLandingEvent.NavigateToInspectionAnswer -> onStartInspection(event.inspectionId)
            }
        }
    }

    PullToRefreshContainer(
        isRefreshing = state.isRefreshing,
        onRefresh = { state.onRefresh() },
        modifier = Modifier
            .fillMaxSize()
            .padding(
                top = paddingValues.calculateTopPadding(),
            )
    ) {
        Column {
            LandingHeader(stringResource(Res.string.auditor))
            HeaderContent()
            if (state.data.data.isNotEmpty()) {
                ContentContainer(
                    paddingValues = paddingValues,
                    color = color,
                    dimension = dimension
                ) {
                    Pagination(
                        page = state.page,
                        pageSize = state.pageSize,
                        total = state.data.total,
                        onPrevious = state::onPreviousPage,
                        onNext = state::onNextPage,
                        modifier = Modifier
                            .padding(top = topPadding)
                            .fillMaxWidth()
                            .fillMaxHeight()
                    ) {
                        LazyColumn(
                            modifier = Modifier
                                .fillMaxHeight()
                        ) {
                            itemContent(
                                data = state.data.data,
                                color = color,
                                dimension = dimension,
                                onClick = { state.onShowBottomSheet(it) }
                            )
                        }
                    }
                }
            } else {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxHeight()
                ) {
                    item {
                        EmptyContent {
                            TextBodySmall(stringResource(Res.string.no_templates_found))
                        }
                    }
                }
            }
        }

        TemplatePreviewContent(
            sheetState = sheetState,
            show = state.showBottomSheet,
            isLoading = state.isLoadingDetailTemplate,
            action = ModalAction.default().copy(
                showAction = true,
                label = stringResource(Res.string.start_inspection),
                onClick = {
                    state.onDismissBottomSheet()
                    state.onStartInspectionClicked(state.detailTemplate.id)
                }
            ),
            onDismiss = { state.onDismissBottomSheet() }
        ) { topPadding ->
            TemplatePreview(
                paddingValues = paddingValues,
                state = state,
                topPadding = topPadding
            )
        }

        LoadingDialog(
            isLoading = state.isLoadingCreateInspection,
            message = stringResource(Res.string.create_inspection)
        )

        LoadingDialog(isLoading = state.isLoading)
    }
}

@Composable
expect fun TemplatePreviewContent(
    sheetState: SheetState,
    show: Boolean = false,
    isLoading: Boolean = false,
    action: ModalActionModel,
    onDismiss: () -> Unit,
    content: @Composable (topPadding: Dp) -> Unit
)

expect fun LazyListScope.itemContent(
    data: List<InspectionTemplateModel>,
    color: ThemeColor,
    dimension: AppDimension,
    onClick: (templateId: String) -> Unit
)

@Composable
expect fun HeaderContent()

@Composable
expect fun ContentContainer(
    paddingValues: PaddingValues,
    color: ThemeColor,
    dimension: AppDimension,
    content: @Composable ColumnScope.() -> Unit
)
